import { Box, Chip, Stack, Typography } from '@mui/material';
import { Activity, useCallback, useEffect, useRef, useState } from 'react';

import { dayjs } from '@ui/Components/Dayjs/dayjs-utils';
import { RX1_PALETTE, RX2_PALETTE } from '@ui/Components/Sdr/canvasRendering';
import SpectrumWaterfallCanvas, {
    type SpectrumWaterfallCanvasHandles,
} from '@ui/Components/Sdr/SpectrumWaterfallCanvas';
import type { AlertSeverity, DeviceStatus, LiveRowPayload } from '@ui/Types/Gnss';
import { FLOORPLAN_SRC } from '@ui/Types/Gnss';
import {
    useGetGnssBaseline,
    useGetGnssDeviceConfig,
    useGetGnssDeviceStatus,
    useGetGnssLayout,
    useGnssStartLive,
    useGnssStartLiveSubscription,
    useGnssStopLiveSubscription,
} from '@ui/Utils/api/Gnss/client';
import getCDNImagePath from '@ui/Utils/getCDNImagePath';

import { getEnvironmentVariable } from '../../Utils/environment';
import { acquireSpectrumWsConnection } from './SpectrumWsConnection';

import type { Dashboard } from '../../Types/AuthServer';
import type { SensorWsConnection } from './SpectrumWsConnection';

type WsListener = (data: unknown) => void;

interface InfoscreenSpectrumViewProps {
    enableLive: boolean;
    infoscreenLayout?: Dashboard['socials'];
    isActive: boolean;
    sensorSerial: string | null;
}

interface SpectrumCanvasCache {
    deviceStatus: DeviceStatus | null;
    drawConfig: { binWidthMHz: number; freqEndMHz: number; freqStartMHz: number } | null;
    frameCounter: number;
    layoutCoord: { xPct: number; yPct: number } | null;
    rx2Rows: number[][];
    sweepRows: number[][];
}

interface RenderedImageBox {
    height: number;
    left: number;
    top: number;
    width: number;
}

type BaselineCompatibility = 'match' | 'mismatch' | 'unknown';

const getSpectrumCanvasCacheStore = (): Record<string, SpectrumCanvasCache> => {
    const win = window as Window & { __spectrumCanvasCache?: Record<string, SpectrumCanvasCache> };
    if (!win.__spectrumCanvasCache) {
        win.__spectrumCanvasCache = {};
    }
    return win.__spectrumCanvasCache;
};

const getOrCreateSpectrumCanvasCache = (sensorId: string): SpectrumCanvasCache => {
    const cacheStore = getSpectrumCanvasCacheStore();
    if (!cacheStore[sensorId]) {
        cacheStore[sensorId] = {
            deviceStatus: null,
            drawConfig: null,
            frameCounter: 0,
            layoutCoord: null,
            rx2Rows: [],
            sweepRows: [],
        };
    }
    return cacheStore[sensorId];
};

export default function InfoscreenSpectrumView({
    enableLive,
    infoscreenLayout,
    isActive,
    sensorSerial,
}: Readonly<InfoscreenSpectrumViewProps>) {
    const { mutate: refreshLayout } = useGetGnssLayout();
    const { mutate: refreshBaseline } = useGetGnssBaseline(sensorSerial, {
        swr: { enabled: Boolean(sensorSerial) },
    });
    const { mutate: refreshDeviceConfig } = useGetGnssDeviceConfig(sensorSerial, {
        swr: { enabled: Boolean(sensorSerial) },
    });
    const { mutate: refreshDeviceStatus } = useGetGnssDeviceStatus(sensorSerial, {
        swr: { enabled: Boolean(sensorSerial) },
    });
    const liveStartAction = useGnssStartLive();
    const subscribeStartAction = useGnssStartLiveSubscription();
    const subscribeStopAction = useGnssStopLiveSubscription();
    const rootContainerRef = useRef<HTMLDivElement | null>(null);
    const drawConfigRef = useRef<{ binWidthMHz: number; freqEndMHz: number; freqStartMHz: number } | null>(null);
    const frameCounterRef = useRef<number>(0);
    const sweepRowsRef = useRef<number[][]>([]);
    const rx2RowsRef = useRef<number[][]>([]);
    const sessionTokenRef = useRef<string | null>(null);
    const startedLiveRef = useRef<boolean>(false);
    const enableLiveRef = useRef<boolean>(enableLive);
    const isActiveRef = useRef<boolean>(isActive);
    const sensorConnectionRef = useRef<SensorWsConnection | null>(null);
    const primaryCanvasRef = useRef<SpectrumWaterfallCanvasHandles | null>(null);
    const secondaryCanvasRef = useRef<SpectrumWaterfallCanvasHandles | null>(null);
    const primaryCursorRef = useRef<{ xContent: number | null }>({ xContent: null });
    const secondaryCursorRef = useRef<{ xContent: number | null }>({ xContent: null });
    const alertMarkersRx1Ref = useRef<{ db: number; freqMHz: number; severity?: AlertSeverity; ts: number }[]>([]);
    const alertMarkersRx2Ref = useRef<{ db: number; freqMHz: number; severity?: AlertSeverity; ts: number }[]>([]);
    const rowsReceivedCountRef = useRef<number>(0);
    const alertTrackedCountRef = useRef<number>(0);
    const lastLiveRowAtRef = useRef<number>(0);
    const lastLiveSensorRef = useRef<string | null>(null);
    const activationSequenceRef = useRef<number>(0);
    const miniMapContainerRef = useRef<HTMLDivElement | null>(null);
    const miniMapImageRef = useRef<HTMLImageElement | null>(null);

    const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'open' | 'closed' | 'error'>('idle');
    const [pxPerMHz, setPxPerMHz] = useState<number>(1.5);
    const [deviceStatus, setDeviceStatus] = useState<DeviceStatus | null>(null);
    const [layoutCoord, setLayoutCoord] = useState<{ xPct: number; yPct: number } | null>(null);
    const [floorplanHeightPx, setFloorplanHeightPx] = useState<number>(0);
    const [logoHeightPx, setLogoHeightPx] = useState<number>(0);
    const [containerWidthPx, setContainerWidthPx] = useState<number>(0);
    const [isCompactLayout, setIsCompactLayout] = useState<boolean>(false);
    const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
    const [sweepOn, setSweepOn] = useState<boolean | null>(null);
    const [liveOn, setLiveOn] = useState<boolean | null>(null);
    const [rowsReceivedCount, setRowsReceivedCount] = useState<number>(0);
    const [alertsTrackedCount, setAlertsTrackedCount] = useState<number>(0);
    const [isReceivingRows, setIsReceivingRows] = useState<boolean>(false);
    const [baselineCompatibility, setBaselineCompatibility] = useState<BaselineCompatibility>('unknown');
    const [showAlerts, setShowAlerts] = useState<boolean>(true);
    const [miniMapRenderedImageBox, setMiniMapRenderedImageBox] = useState<RenderedImageBox>({
        height: 0,
        left: 0,
        top: 0,
        width: 0,
    });
    const [now, setNow] = useState(() => dayjs());
    enableLiveRef.current = enableLive;

    const logoPrimary = getCDNImagePath('logos/wise_device_blue_whitetext.png');
    const logoSecondary = getCDNImagePath('logos/missing-link_logo.png');
    const logoDevice = getCDNImagePath('devices/spectrum_wise_v3.2.png');
    const timezone = infoscreenLayout?.tz || undefined;

    const totalMHz = 6000;
    const leftPadPx = 24;
    const rightPadPx = 24;
    const labelAreaPx = 56;
    const minPxPerMHz = 0.0001;
    const maxPxPerMHzZoom = 750;
    const receivingRowsThresholdMs = 4000;
    const parseBooleanState = (value: unknown): boolean | null => {
        if (typeof value === 'boolean') {
            return value;
        }
        const numeric = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : null;
        if (!Number.isFinite(numeric ?? Number.NaN)) {
            return null;
        }
        if (numeric === 1) {
            return true;
        }
        if (numeric === 0) {
            return false;
        }
        return null;
    };

    const toChipColor = (value: boolean | null): 'success' | 'error' | 'default' => {
        if (value === null) {
            return 'default';
        }
        return value ? 'success' : 'error';
    };

    const toOnOffLabel = (name: string, value: boolean | null): string => {
        if (value === null) {
            return `${name} ?`;
        }
        return value ? `${name} ON` : `${name} OFF`;
    };

    const getStatusChipSx = (isUnknown: boolean) => {
        return {
            height: 16,
            ...(isUnknown
                ? {
                      bgcolor: 'rgba(117,117,117,0.32)',
                      borderColor: 'rgba(158,158,158,0.7)',
                      color: '#e0e0e0',
                  }
                : {}),
            '& .MuiChip-label': { fontSize: '0.52rem', px: 0.5 },
        };
    };

    const compareHz = (left: number, right: number): boolean => {
        return Math.abs(left - right) < 0.5;
    };

    const parseDeviceFrequencyMHz = (value: unknown): number | null => {
        const numeric = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : null;
        if (!Number.isFinite(numeric ?? Number.NaN)) {
            return null;
        }
        if (numeric !== null && numeric > 100_000) {
            return numeric / 1_000_000;
        }
        return numeric;
    };

    const formatMHz = (valueMHz: number): string => {
        const roundedSingleDecimal = Math.round(valueMHz * 10) / 10;
        if (Math.abs(roundedSingleDecimal - Math.round(roundedSingleDecimal)) < 0.01) {
            return Math.round(roundedSingleDecimal).toString();
        }
        return roundedSingleDecimal.toFixed(1);
    };

    const getRangeZoomConfig = useCallback(
        (containerWidth: number, startMHz: number, stopMHz: number) => {
            const effectiveLeftPad = leftPadPx + labelAreaPx;
            const available = Math.max(1, containerWidth - effectiveLeftPad - rightPadPx);

            const clampedStartMHz = Math.max(0, Math.min(totalMHz, startMHz));
            const clampedStopMHz = Math.max(clampedStartMHz, Math.min(totalMHz, stopMHz));
            const rangeMHz = Math.max(0.0001, clampedStopMHz - clampedStartMHz);

            const preferredSideMargin = Math.max(24, Math.min(96, Math.floor(available * 0.05)));
            const leftMarginPx = clampedStartMHz <= 0 ? 0 : preferredSideMargin;
            const rightMarginPx = clampedStopMHz >= totalMHz ? 0 : preferredSideMargin;
            const usable = Math.max(1, available - leftMarginPx - rightMarginPx);

            return {
                leftMarginPx,
                pxPerMHz: usable / rangeMHz,
                startMHz: clampedStartMHz,
            };
        },
        [labelAreaPx, leftPadPx, rightPadPx, totalMHz],
    );

    const applyRangeZoom = useCallback(
        (startMHz: number, stopMHz: number) => {
            let attempts = 0;

            const tryApply = () => {
                attempts += 1;
                const container = primaryCanvasRef.current?.container;
                if (!container || container.clientWidth <= 0) {
                    if (attempts < 5) {
                        requestAnimationFrame(tryApply);
                    }
                    return;
                }

                const zoomConfig = getRangeZoomConfig(container.clientWidth, startMHz, stopMHz);
                const pxLocal = zoomConfig.pxPerMHz;
                const pxClamped = Math.max(minPxPerMHz, Math.min(maxPxPerMHzZoom, pxLocal));
                setPxPerMHz(pxClamped);

                const applyScroll = (scrollAttempt: number) => {
                    const containers = [
                        primaryCanvasRef.current?.container,
                        secondaryCanvasRef.current?.container,
                    ].filter((item): item is HTMLDivElement => Boolean(item));

                    containers.forEach((entry) => {
                        const raw = Math.floor(zoomConfig.startMHz * pxClamped - zoomConfig.leftMarginPx);
                        const maxScroll = Math.max(0, entry.scrollWidth - entry.clientWidth);
                        entry.scrollLeft = Math.max(0, Math.min(maxScroll, raw));
                    });

                    if (scrollAttempt < 12) {
                        requestAnimationFrame(() => {
                            applyScroll(scrollAttempt + 1);
                        });
                    }
                };

                requestAnimationFrame(() => {
                    applyScroll(0);
                });
            };

            tryApply();
        },
        [getRangeZoomConfig],
    );

    const resetSpectrumRuntimeState = useCallback(() => {
        sweepRowsRef.current.splice(0, sweepRowsRef.current.length);
        rx2RowsRef.current.splice(0, rx2RowsRef.current.length);
        alertMarkersRx1Ref.current.splice(0, alertMarkersRx1Ref.current.length);
        alertMarkersRx2Ref.current.splice(0, alertMarkersRx2Ref.current.length);
        rowsReceivedCountRef.current = 0;
        alertTrackedCountRef.current = 0;
        lastLiveRowAtRef.current = 0;

        frameCounterRef.current = frameCounterRef.current + 1;

        setRowsReceivedCount(0);
        setAlertsTrackedCount(0);
        setIsReceivingRows(false);
    }, []);

    useEffect(() => {
        if (!sensorSerial) {
            sweepRowsRef.current = [];
            rx2RowsRef.current = [];
            drawConfigRef.current = null;
            alertMarkersRx1Ref.current = [];
            alertMarkersRx2Ref.current = [];
            rowsReceivedCountRef.current = 0;
            alertTrackedCountRef.current = 0;
            lastLiveRowAtRef.current = 0;
            frameCounterRef.current = frameCounterRef.current + 1;
            setDeviceStatus(null);
            setLayoutCoord(null);
            setSweepOn(null);
            setLiveOn(null);
            setIsSubscribed(false);
            setRowsReceivedCount(0);
            setAlertsTrackedCount(0);
            setIsReceivingRows(false);
            setBaselineCompatibility('unknown');
            return;
        }

        const cache = getOrCreateSpectrumCanvasCache(sensorSerial);
        sweepRowsRef.current = cache.sweepRows;
        rx2RowsRef.current = cache.rx2Rows;
        drawConfigRef.current = cache.drawConfig;
        frameCounterRef.current = cache.frameCounter;
        setDeviceStatus(cache.deviceStatus);
        setLayoutCoord(cache.layoutCoord);
        alertMarkersRx1Ref.current = [];
        alertMarkersRx2Ref.current = [];
        rowsReceivedCountRef.current = 0;
        alertTrackedCountRef.current = 0;
        lastLiveRowAtRef.current = 0;
        setRowsReceivedCount(0);
        setAlertsTrackedCount(0);
        setIsReceivingRows(false);
        setIsSubscribed(false);
        setBaselineCompatibility('unknown');
    }, [sensorSerial]);

    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);

    useEffect(() => {
        if (!sensorSerial || !isActive) {
            return;
        }

        let cancelled = false;
        const fetchLayout = async () => {
            try {
                const data = (await refreshLayout()) as
                    | { layout?: { coords?: readonly { device_id?: string; x_pct?: number; y_pct?: number }[] } }
                    | undefined;
                if (cancelled || !data) {
                    return;
                }
                const coords = data.layout?.coords ?? [];
                const match = coords.find((coord) => coord.device_id === sensorSerial) || null;
                if (match && typeof match.x_pct === 'number' && typeof match.y_pct === 'number') {
                    const nextLayoutCoord = { xPct: match.x_pct, yPct: match.y_pct };
                    setLayoutCoord(nextLayoutCoord);
                    const cache = getOrCreateSpectrumCanvasCache(sensorSerial);
                    cache.layoutCoord = nextLayoutCoord;
                } else {
                    setLayoutCoord(null);
                    const cache = getOrCreateSpectrumCanvasCache(sensorSerial);
                    cache.layoutCoord = null;
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to fetch layout coordinates:', error);
                }
            }
        };

        void fetchLayout();
        return () => {
            cancelled = true;
        };
    }, [sensorSerial, isActive]);

    useEffect(() => {
        const id = setInterval(() => {
            setNow(dayjs());
            const isReceivingNow = Date.now() - lastLiveRowAtRef.current <= receivingRowsThresholdMs;
            setIsReceivingRows((previousValue) => {
                if (previousValue === isReceivingNow) {
                    return previousValue;
                }
                return isReceivingNow;
            });
            setRowsReceivedCount((previousValue) => {
                if (previousValue === rowsReceivedCountRef.current) {
                    return previousValue;
                }
                return rowsReceivedCountRef.current;
            });
            setAlertsTrackedCount((previousValue) => {
                if (previousValue === alertTrackedCountRef.current) {
                    return previousValue;
                }
                return alertTrackedCountRef.current;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [receivingRowsThresholdMs]);

    useEffect(() => {
        if (!sensorSerial || !isActive) {
            setBaselineCompatibility('unknown');
            return;
        }

        let cancelled = false;
        const fetchBaselineCompatibility = async () => {
            try {
                const [baseline, configPayload] = await Promise.all([refreshBaseline(), refreshDeviceConfig()]);
                if (cancelled) {
                    return;
                }
                const config = configPayload?.sweepConfig;
                if (!baseline || !config) {
                    setBaselineCompatibility('unknown');
                    return;
                }

                const startHzMatch = compareHz(baseline.f_start_hz, config.f_start_mhz * 1_000_000);
                const stopHzMatch = compareHz(baseline.f_stop_hz, config.f_stop_mhz * 1_000_000);
                const stepHzMatch = compareHz(baseline.f_step_hz, config.f_step_mhz * 1_000_000);

                setBaselineCompatibility(startHzMatch && stopHzMatch && stepHzMatch ? 'match' : 'mismatch');
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to evaluate baseline compatibility:', error);
                    setBaselineCompatibility('unknown');
                }
            }
        };

        void fetchBaselineCompatibility();
        return () => {
            cancelled = true;
        };
    }, [isActive, refreshBaseline, refreshDeviceConfig, sensorSerial]);

    const updateMiniMapRenderedImageBox = useCallback(() => {
        const containerElement = miniMapContainerRef.current;
        const imageElement = miniMapImageRef.current;
        if (!containerElement || !imageElement) {
            return;
        }

        const containerWidth = containerElement.clientWidth;
        const containerHeight = containerElement.clientHeight;
        if (containerWidth <= 0 || containerHeight <= 0) {
            return;
        }

        const imageNaturalWidth = imageElement.naturalWidth;
        const imageNaturalHeight = imageElement.naturalHeight;
        if (imageNaturalWidth <= 0 || imageNaturalHeight <= 0) {
            setMiniMapRenderedImageBox({
                height: containerHeight,
                left: 0,
                top: 0,
                width: containerWidth,
            });
            return;
        }

        const containerAspectRatio = containerWidth / containerHeight;
        const imageAspectRatio = imageNaturalWidth / imageNaturalHeight;

        if (imageAspectRatio > containerAspectRatio) {
            const renderedWidth = containerWidth;
            const renderedHeight = containerWidth / imageAspectRatio;
            setMiniMapRenderedImageBox({
                height: renderedHeight,
                left: 0,
                top: (containerHeight - renderedHeight) / 2,
                width: renderedWidth,
            });
            return;
        }

        const renderedHeight = containerHeight;
        const renderedWidth = containerHeight * imageAspectRatio;
        setMiniMapRenderedImageBox({
            height: renderedHeight,
            left: (containerWidth - renderedWidth) / 2,
            top: 0,
            width: renderedWidth,
        });
    }, []);

    useEffect(() => {
        updateMiniMapRenderedImageBox();

        const resizeObserver = new ResizeObserver(() => {
            updateMiniMapRenderedImageBox();
        });

        const containerElement = miniMapContainerRef.current;
        if (containerElement) {
            resizeObserver.observe(containerElement);
        }

        window.addEventListener('resize', updateMiniMapRenderedImageBox);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateMiniMapRenderedImageBox);
        };
    }, [updateMiniMapRenderedImageBox]);

    useEffect(() => {
        const rootElement = rootContainerRef.current;
        if (!rootElement) {
            return;
        }

        const updateFloorplanHeight = () => {
            const rootHeight = Math.max(1, rootElement.clientHeight);
            const rootWidth = Math.max(1, rootElement.clientWidth);
            setContainerWidthPx(rootWidth);
            const shouldUseCompactLayout = rootWidth < 2100 || rootHeight < 1200;
            setIsCompactLayout((previousValue) => {
                if (previousValue === shouldUseCompactLayout) {
                    return previousValue;
                }
                return shouldUseCompactLayout;
            });

            const floorplanRatio = shouldUseCompactLayout ? 0.12 : 0.16;
            const floorplanMin = shouldUseCompactLayout ? 72 : 96;
            const floorplanMax = shouldUseCompactLayout ? 132 : 180;
            const logoRatio = shouldUseCompactLayout ? 0.028 : 0.04;
            const logoMin = shouldUseCompactLayout ? 20 : 26;
            const logoMax = shouldUseCompactLayout ? 34 : 46;

            const nextFloorplanHeight = Math.max(
                floorplanMin,
                Math.min(floorplanMax, Math.round(rootHeight * floorplanRatio)),
            );
            const nextLogoHeight = Math.max(logoMin, Math.min(logoMax, Math.round(rootHeight * logoRatio)));

            setFloorplanHeightPx(nextFloorplanHeight);
            setLogoHeightPx(nextLogoHeight);
        };

        updateFloorplanHeight();

        const resizeObserver = new ResizeObserver(() => {
            updateFloorplanHeight();
        });

        resizeObserver.observe(rootElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!sensorSerial || !enableLive) {
            return;
        }

        const wsHost = getEnvironmentVariable('EXPO_PUBLIC_SDR_WS_HOST');
        if (!wsHost) {
            console.error('Missing EXPO_PUBLIC_SDR_WS_HOST for spectrum view');
            return;
        }

        let isMounted = true;
        const bufferCap = 600;
        const { connection, release } = acquireSpectrumWsConnection(wsHost, setConnectionState);
        sensorConnectionRef.current = connection;
        sessionTokenRef.current = connection.sessionToken || connection.connectionId;

        const handleLiveRow = (payload: LiveRowPayload) => {
            if (!isMounted) {
                return;
            }
            if (payload.device_id && payload.device_id !== sensorSerial) {
                return;
            }

            const startHz = payload.f_start_hz;
            const stepHz = payload.f_step_hz;
            const bins =
                typeof payload.bins === 'number'
                    ? payload.bins
                    : Array.isArray(payload.rx1_dbfs)
                      ? payload.rx1_dbfs.length
                      : Array.isArray(payload.rx2_dbfs)
                        ? payload.rx2_dbfs.length
                        : 0;

            if (typeof startHz === 'number' && typeof stepHz === 'number' && typeof bins === 'number') {
                const freqStartMHz = startHz / 1_000_000;
                const binWidthMHz = stepHz / 1_000_000;
                const freqEndMHz = freqStartMHz + binWidthMHz * bins;
                const prev = drawConfigRef.current;

                if (
                    !prev ||
                    prev.freqStartMHz !== freqStartMHz ||
                    prev.binWidthMHz !== binWidthMHz ||
                    prev.freqEndMHz !== freqEndMHz
                ) {
                    const nextDrawConfig = {
                        binWidthMHz,
                        freqEndMHz,
                        freqStartMHz,
                    };
                    drawConfigRef.current = nextDrawConfig;
                    const drawConfigCache = getOrCreateSpectrumCanvasCache(sensorSerial);
                    drawConfigCache.drawConfig = nextDrawConfig;
                }
            }

            const rx1 = Array.isArray(payload.rx1_dbfs) ? payload.rx1_dbfs : null;
            const rx2 = Array.isArray(payload.rx2_dbfs) ? payload.rx2_dbfs : null;

            if ((rx1 && rx1.length > 0) || (rx2 && rx2.length > 0)) {
                rowsReceivedCountRef.current = rowsReceivedCountRef.current + 1;
                lastLiveRowAtRef.current = Date.now();
                setIsReceivingRows(true);
            }

            if (rx1 && rx1.length > 0) {
                sweepRowsRef.current.push(rx1);
                if (sweepRowsRef.current.length > bufferCap) {
                    sweepRowsRef.current.splice(0, sweepRowsRef.current.length - bufferCap);
                }
                frameCounterRef.current = frameCounterRef.current + 1;
                const rx1Cache = getOrCreateSpectrumCanvasCache(sensorSerial);
                rx1Cache.frameCounter = frameCounterRef.current;
            }

            if (rx2 && rx2.length > 0) {
                rx2RowsRef.current.push(rx2);
                if (rx2RowsRef.current.length > bufferCap) {
                    rx2RowsRef.current.splice(0, rx2RowsRef.current.length - bufferCap);
                }
                frameCounterRef.current = frameCounterRef.current + 1;
                const rx2Cache = getOrCreateSpectrumCanvasCache(sensorSerial);
                rx2Cache.frameCounter = frameCounterRef.current;
            }

            try {
                const toSeverity = (severityValue: number | undefined): AlertSeverity => {
                    return severityValue === 2 ? 'high' : 'medium';
                };
                const tsMs = typeof payload.t_ns === 'number' ? Math.floor(payload.t_ns / 1_000_000) : Date.now();

                if (
                    Array.isArray(payload.alerts_rx1) &&
                    rx1 &&
                    typeof startHz === 'number' &&
                    typeof stepHz === 'number'
                ) {
                    for (const alertItem of payload.alerts_rx1) {
                        if (alertMarkersRx1Ref.current.length >= 1000) {
                            break;
                        }
                        const freqMHz = (startHz + alertItem.bin * stepHz) / 1_000_000;
                        const db = rx1[alertItem.bin] ?? -120;
                        alertMarkersRx1Ref.current.push({
                            db,
                            freqMHz,
                            severity: toSeverity(alertItem.severity),
                            ts: tsMs,
                        });
                        if (alertTrackedCountRef.current < 1000) {
                            alertTrackedCountRef.current = alertTrackedCountRef.current + 1;
                        }
                    }
                    frameCounterRef.current = frameCounterRef.current + 1;
                }

                if (
                    Array.isArray(payload.alerts_rx2) &&
                    rx2 &&
                    typeof startHz === 'number' &&
                    typeof stepHz === 'number'
                ) {
                    for (const alertItem of payload.alerts_rx2) {
                        if (alertMarkersRx2Ref.current.length >= 1000) {
                            break;
                        }
                        const freqMHz = (startHz + alertItem.bin * stepHz) / 1_000_000;
                        const db = rx2[alertItem.bin] ?? -120;
                        alertMarkersRx2Ref.current.push({
                            db,
                            freqMHz,
                            severity: toSeverity(alertItem.severity),
                            ts: tsMs,
                        });
                        if (alertTrackedCountRef.current < 1000) {
                            alertTrackedCountRef.current = alertTrackedCountRef.current + 1;
                        }
                    }
                    frameCounterRef.current = frameCounterRef.current + 1;
                }
            } catch (error) {
                console.error('Failed to map live alert markers:', error);
            }
        };

        const listener: WsListener = (data) => {
            if (!isMounted || !data || typeof data !== 'object') {
                return;
            }
            if (!isActiveRef.current) {
                return;
            }

            const message = data as { payload?: unknown; type?: string };
            if (message.type === 'live_row') {
                handleLiveRow(message as LiveRowPayload);
                return;
            }

            if (message.payload && typeof message.payload === 'object') {
                const payload = message.payload as { type?: string };
                if (payload.type === 'live_row') {
                    handleLiveRow(message.payload as LiveRowPayload);
                }
            }
        };

        connection.listeners.add(listener);

        return () => {
            isMounted = false;
            connection.listeners.delete(listener);
            release();
        };
    }, [sensorSerial, enableLive]);

    useEffect(() => {
        if (!sensorSerial || !enableLive) {
            return;
        }

        const socketReady = sensorConnectionRef.current?.socket?.readyState === WebSocket.OPEN;
        if (!socketReady) {
            return;
        }

        activationSequenceRef.current = activationSequenceRef.current + 1;
        const sequenceId = activationSequenceRef.current;

        void (async () => {
            try {
                if (sequenceId !== activationSequenceRef.current) {
                    return;
                }

                const token = sensorConnectionRef.current?.sessionToken || sensorConnectionRef.current?.connectionId;
                if (!token) {
                    return;
                }
                sessionTokenRef.current = token;

                const previousSensor = lastLiveSensorRef.current;

                if (!isActive) {
                    if (previousSensor && startedLiveRef.current) {
                        await subscribeStopAction.trigger({ deviceSerial: previousSensor, session: token });
                        startedLiveRef.current = false;
                        lastLiveSensorRef.current = null;
                        setIsSubscribed(false);
                    }
                    return;
                }

                if (previousSensor === sensorSerial && startedLiveRef.current) {
                    return;
                }

                if (previousSensor && previousSensor !== sensorSerial) {
                    await subscribeStopAction.trigger({ deviceSerial: previousSensor, session: token });
                    startedLiveRef.current = false;
                    lastLiveSensorRef.current = null;
                    setIsSubscribed(false);
                }

                if (sequenceId !== activationSequenceRef.current) {
                    return;
                }

                const status = await refreshDeviceStatus();
                if (!status) {
                    return;
                }
                setDeviceStatus(status);
                const statusCache = getOrCreateSpectrumCanvasCache(sensorSerial);
                statusCache.deviceStatus = status;
                const sweepEnabled = parseBooleanState(status.enabled);
                const liveEnabled = parseBooleanState(status.live);
                setSweepOn(sweepEnabled);
                setLiveOn(liveEnabled);

                const startMHz = parseDeviceFrequencyMHz(status.start);
                const stopMHz = parseDeviceFrequencyMHz(status.stop);
                if (startMHz !== null && stopMHz !== null && stopMHz > startMHz) {
                    applyRangeZoom(startMHz, stopMHz);
                }

                const liveValue = typeof status.live === 'string' ? Number(status.live) : status.live;
                const isLiveEnabled = Number(liveValue) === 1;

                await new Promise<void>((resolve) => {
                    setTimeout(resolve, 1000);
                });

                if (sequenceId !== activationSequenceRef.current) {
                    return;
                }

                if (isLiveEnabled) {
                    try {
                        await subscribeStartAction.trigger({ deviceSerial: sensorSerial, session: token });
                        startedLiveRef.current = true;
                        lastLiveSensorRef.current = sensorSerial;
                        setIsSubscribed(true);
                    } catch (error) {
                        if (error instanceof Error && error.message.includes('Network error')) {
                            throw error;
                        }

                        const fallbackStartRes = await liveStartAction.trigger({
                            deviceSerial: sensorSerial,
                            session: token,
                        });
                        if (fallbackStartRes.ok) {
                            startedLiveRef.current = true;
                            lastLiveSensorRef.current = sensorSerial;
                            setIsSubscribed(true);
                            setLiveOn(true);
                        }
                    }
                } else {
                    const startRes = await liveStartAction.trigger({ deviceSerial: sensorSerial, session: token });
                    if (startRes.ok) {
                        startedLiveRef.current = true;
                        lastLiveSensorRef.current = sensorSerial;
                        setIsSubscribed(true);
                        setLiveOn(true);
                    }
                }
            } catch (error) {
                console.error('Failed to start spectrum live feed:', error);
            }
        })();

        return () => {
            activationSequenceRef.current = activationSequenceRef.current + 1;
        };
    }, [sensorSerial, isActive, enableLive, connectionState, applyRangeZoom]);

    useEffect(() => {
        return () => {
            const sensor = lastLiveSensorRef.current;
            if (!sensor) {
                return;
            }

            const stopToken = sessionTokenRef.current ?? undefined;
            void subscribeStopAction.trigger({ deviceSerial: sensor, session: stopToken });
            startedLiveRef.current = false;
            lastLiveSensorRef.current = null;
            setIsSubscribed(false);
        };
    }, [subscribeStopAction]);

    useEffect(() => {
        if (connectionState !== 'open') {
            setIsSubscribed(false);
        }
    }, [connectionState]);

    const timeLabel = timezone ? now.tz(timezone).format('HH:mm:ss') : now.format('HH:mm:ss');
    const dateLabel = timezone ? now.tz(timezone).format('ddd, MMM D') : now.format('ddd, MMM D');
    const startMHz = parseDeviceFrequencyMHz(deviceStatus?.start);
    const stopMHz = parseDeviceFrequencyMHz(deviceStatus?.stop);
    const rangeLabel =
        startMHz !== null && stopMHz !== null ? `${formatMHz(startMHz)}-${formatMHz(stopMHz)} MHz` : null;
    const sensorNumberLabel = (() => {
        if (!sensorSerial) {
            return null;
        }
        const numberMatch = /^MLS0040000(\d+)123-SPEC$/.exec(sensorSerial);
        if (!numberMatch?.[1]) {
            return null;
        }
        const parsedNumber = Number.parseInt(numberMatch[1], 10);
        if (Number.isNaN(parsedNumber)) {
            return null;
        }
        return parsedNumber.toString();
    })();
    const wsOn = connectionState === 'open';
    const baselineCompatibilityLabel =
        baselineCompatibility === 'match'
            ? 'Baseline Match'
            : baselineCompatibility === 'mismatch'
              ? 'Baseline Mismatch'
              : 'Baseline Unknown';
    const baselineCompatibilityColor =
        baselineCompatibility === 'match' ? 'success' : baselineCompatibility === 'mismatch' ? 'warning' : 'default';
    const effectiveWidth = Math.max(640, containerWidthPx || 1280);
    const deviceInfoFontPx = isCompactLayout
        ? Math.max(7, Math.min(10, Math.round(effectiveWidth * 0.0058)))
        : Math.max(9, Math.min(13, Math.round(effectiveWidth * 0.0062)));
    const timeFontPx = isCompactLayout
        ? Math.max(16, Math.min(28, Math.round(effectiveWidth * 0.017)))
        : Math.max(20, Math.min(38, Math.round(effectiveWidth * 0.019)));
    const dateFontPx = isCompactLayout
        ? Math.max(9, Math.min(12, Math.round(effectiveWidth * 0.0075)))
        : Math.max(10, Math.min(15, Math.round(effectiveWidth * 0.0082)));
    const effectiveMiniMapWidth =
        miniMapRenderedImageBox.width > 0 ? miniMapRenderedImageBox.width : isCompactLayout ? 150 : 180;
    const miniMapMarkerSizePx = Math.max(8, Math.min(12, Math.round(effectiveMiniMapWidth * 0.055)));
    const miniMapMarkerFontPx = Math.max(6, Math.min(8, Math.round(miniMapMarkerSizePx * 0.5)));
    const miniMapMarkerShadowPx = Math.max(4, Math.round(miniMapMarkerSizePx * 0.45));

    return (
        <Activity mode={isActive ? 'visible' : 'hidden'}>
            <Box
                ref={rootContainerRef}
                sx={{
                    backgroundColor: '#000000',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    width: '100%',
                }}
            >
                {!sensorSerial ? (
                    <Stack
                        sx={{
                            alignItems: 'center',
                            flex: 1,
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                color: 'text.secondary',
                            }}
                            variant="h5"
                        >
                            Select a spectrum sensor to start streaming
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={0} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <Box
                            sx={{
                                borderBottom: '1px solid #1a1a1a',
                                flexShrink: 0,
                                maxHeight: '50%',
                                minHeight: floorplanHeightPx > 0 ? `${floorplanHeightPx.toString()}px` : '132px',
                                overflow: 'hidden',
                                pb: 0,
                                position: 'relative',
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={8}
                                sx={{
                                    alignItems: 'stretch',
                                    bottom: 0,
                                    left: 0,
                                    maxWidth: isCompactLayout ? '76%' : '68%',
                                    minWidth: 0,
                                    position: 'absolute',
                                    pr: 1,
                                    top: 0,
                                    width: 'fit-content',
                                }}
                            >
                                <Stack
                                    spacing={isCompactLayout ? 0.5 : 0.75}
                                    sx={{
                                        maxWidth: isCompactLayout ? 520 : 640,
                                        minWidth: isCompactLayout ? 180 : 240,
                                        width: 'fit-content',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            height: logoHeightPx > 0 ? `${logoHeightPx.toString()}px` : '32px',
                                            maxWidth: '100%',
                                            minWidth: isCompactLayout ? 100 : 120,
                                        }}
                                    >
                                        <Box
                                            alt="Primary logo"
                                            component="img"
                                            src={logoPrimary}
                                            sx={{
                                                flexShrink: 0,
                                                height: '100%',
                                                objectFit: 'contain',
                                                width: isCompactLayout
                                                    ? 'clamp(110px, 72%, 260px)'
                                                    : 'clamp(140px, 76%, 300px)',
                                            }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            borderRadius: 1,
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={isCompactLayout ? 0.55 : 0.8}
                                            sx={{
                                                alignItems: 'stretch',
                                                height: '100%',
                                                minHeight: 0,
                                                px: 0.5,
                                                py: isCompactLayout ? 0.15 : 0,
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    flex: '0 0 auto',
                                                    justifyContent: 'center',
                                                    minWidth: isCompactLayout ? 48 : 64,
                                                    width: isCompactLayout ? 56 : 74,
                                                }}
                                            >
                                                <Box
                                                    alt="Device"
                                                    component="img"
                                                    src={logoDevice}
                                                    sx={{
                                                        display: 'block',
                                                        flexShrink: 0,
                                                        height: '100%',
                                                        maxHeight: isCompactLayout ? 84 : 98,
                                                        maxWidth: isCompactLayout ? 96 : 124,
                                                        objectFit: 'contain',
                                                        width: '100%',
                                                    }}
                                                />
                                            </Box>

                                            <Stack
                                                spacing={isCompactLayout ? 0.15 : 0.25}
                                                sx={{
                                                    alignItems: 'flex-start',
                                                    color: '#ffffff',
                                                    flex: 1,
                                                    justifyContent: 'center',
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    pr: 0.25,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        display: 'block',
                                                        fontSize: `${deviceInfoFontPx.toString()}px`,
                                                        lineHeight: 1.1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        width: '100%',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {sensorSerial}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        display: 'block',
                                                        fontSize: `${deviceInfoFontPx.toString()}px`,
                                                        lineHeight: 1.1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        width: '100%',
                                                    }}
                                                >
                                                    {rangeLabel ?? 'Range unavailable'}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        display: 'block',
                                                        fontSize: `${deviceInfoFontPx.toString()}px`,
                                                        lineHeight: 1.1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        width: '100%',
                                                    }}
                                                >
                                                    Receiving: {isReceivingRows ? 'Active' : 'Idle'} • Rows:{' '}
                                                    {rowsReceivedCount}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Stack>

                                <Stack
                                    spacing={0.35}
                                    sx={{
                                        alignSelf: 'stretch',
                                        flex: '0 0 auto',
                                        justifyContent: 'space-between',
                                        minWidth: isCompactLayout ? 190 : 250,
                                        py: 2,
                                        width: 'fit-content',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={0.35}
                                        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                                    >
                                        <Chip
                                            clickable
                                            color={showAlerts ? 'success' : 'error'}
                                            label={showAlerts ? 'Alerts ON' : 'Alerts OFF'}
                                            onClick={() => {
                                                setShowAlerts((previousValue) => {
                                                    return !previousValue;
                                                });
                                            }}
                                            size="small"
                                            sx={{
                                                '& .MuiChip-label': { fontSize: '0.52rem', px: 0.5 },
                                                height: 16,
                                            }}
                                            variant={showAlerts ? 'filled' : 'outlined'}
                                        />
                                        <Chip
                                            clickable
                                            color="warning"
                                            label="Reset"
                                            onClick={() => {
                                                resetSpectrumRuntimeState();
                                            }}
                                            size="small"
                                            sx={{
                                                '& .MuiChip-label': { fontSize: '0.52rem', px: 0.5 },
                                                height: 16,
                                            }}
                                            variant="outlined"
                                        />
                                    </Stack>

                                    <Stack spacing={0.25} sx={{ width: '100%' }}>
                                        <Stack direction="row" spacing={0.25} sx={{ flexWrap: 'wrap' }}>
                                            <Chip
                                                color={toChipColor(sweepOn)}
                                                label={toOnOffLabel('SWEEP', sweepOn)}
                                                size="small"
                                                sx={getStatusChipSx(sweepOn === null)}
                                                variant={sweepOn === null ? 'outlined' : 'filled'}
                                            />
                                            <Chip
                                                color={toChipColor(liveOn)}
                                                label={toOnOffLabel('LIVE', liveOn)}
                                                size="small"
                                                sx={getStatusChipSx(liveOn === null)}
                                                variant={liveOn === null ? 'outlined' : 'filled'}
                                            />
                                            <Chip
                                                color={toChipColor(isSubscribed)}
                                                label={toOnOffLabel('SUB', isSubscribed)}
                                                size="small"
                                                sx={getStatusChipSx(false)}
                                                variant="filled"
                                            />
                                            <Chip
                                                color={wsOn ? 'success' : 'error'}
                                                label={wsOn ? 'WS ON' : 'WS OFF'}
                                                size="small"
                                                sx={{
                                                    '& .MuiChip-label': { fontSize: '0.52rem', px: 0.5 },
                                                    height: 16,
                                                }}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={0.25} sx={{ flexWrap: 'wrap' }}>
                                            <Chip
                                                color={baselineCompatibilityColor}
                                                label={baselineCompatibilityLabel}
                                                size="small"
                                                sx={getStatusChipSx(baselineCompatibility === 'unknown')}
                                                variant={baselineCompatibility === 'unknown' ? 'outlined' : 'filled'}
                                            />
                                            <Chip
                                                color={alertsTrackedCount >= 1000 ? 'warning' : 'default'}
                                                label={`Alerts ${alertsTrackedCount.toString()} / 1000`}
                                                size="small"
                                                sx={getStatusChipSx(alertsTrackedCount === 0)}
                                                variant={alertsTrackedCount === 0 ? 'outlined' : 'filled'}
                                            />
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Stack>

                            <Stack
                                spacing={isCompactLayout ? 0.15 : 0.25}
                                sx={{
                                    alignItems: 'center',
                                    left: '50%',
                                    minWidth: 120,
                                    pointerEvents: 'none',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#ffffff',
                                        fontSize: `${timeFontPx.toString()}px`,
                                        fontWeight: 600,
                                    }}
                                >
                                    {timeLabel}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#ffffff',
                                        fontSize: `${dateFontPx.toString()}px`,
                                    }}
                                >
                                    {dateLabel}
                                </Typography>
                            </Stack>

                            <Stack
                                direction="row"
                                spacing={isCompactLayout ? 1 : 2}
                                sx={{
                                    alignItems: 'flex-start',
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: logoHeightPx > 0 ? `${logoHeightPx.toString()}px` : '32px',
                                        width: isCompactLayout
                                            ? 'clamp(100px, 14%, 200px)'
                                            : 'clamp(120px, 16%, 260px)',
                                    }}
                                >
                                    <Box
                                        alt="Secondary logo"
                                        component="img"
                                        src={logoSecondary}
                                        sx={{ height: '100%', objectFit: 'contain', width: '100%' }}
                                    />
                                </Box>

                                <Box
                                    ref={miniMapContainerRef}
                                    sx={{
                                        border: '1px solid #1a1a1a',
                                        borderRadius: 1,
                                        display: 'inline-block',
                                        height: floorplanHeightPx > 0 ? `${floorplanHeightPx.toString()}px` : '160px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        width: isCompactLayout
                                            ? 'clamp(108px, 14vw, 190px)'
                                            : 'clamp(132px, 15vw, 220px)',
                                    }}
                                >
                                    <Box
                                        alt="Layout"
                                        component="img"
                                        onLoad={() => {
                                            updateMiniMapRenderedImageBox();
                                        }}
                                        ref={miniMapImageRef}
                                        src={FLOORPLAN_SRC}
                                        sx={{
                                            display: 'block',
                                            height: '100%',
                                            objectFit: 'contain',
                                            width: '100%',
                                        }}
                                    />
                                    {layoutCoord ? (
                                        <Box
                                            sx={{
                                                alignItems: 'center',
                                                backgroundColor: '#00e5ff',
                                                borderRadius: '50%',
                                                boxShadow: `0 0 ${miniMapMarkerShadowPx.toString()}px rgba(0,229,255,0.9)`,
                                                color: '#001018',
                                                display: 'flex',
                                                fontSize: `${miniMapMarkerFontPx.toString()}px`,
                                                fontWeight: 700,
                                                height: miniMapMarkerSizePx,
                                                justifyContent: 'center',
                                                left: `${(
                                                    miniMapRenderedImageBox.left +
                                                    (miniMapRenderedImageBox.width * layoutCoord.xPct) / 100
                                                ).toString()}px`,
                                                position: 'absolute',
                                                top: `${(
                                                    miniMapRenderedImageBox.top +
                                                    (miniMapRenderedImageBox.height * layoutCoord.yPct) / 100
                                                ).toString()}px`,
                                                transform: 'translate(-50%, -50%)',
                                                width: miniMapMarkerSizePx,
                                            }}
                                        >
                                            {sensorNumberLabel}
                                        </Box>
                                    ) : null}
                                </Box>
                            </Stack>
                        </Box>
                        <Stack spacing={0} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                                <SpectrumWaterfallCanvas
                                    alertMarkersRef={
                                        showAlerts && baselineCompatibility === 'match' ? alertMarkersRx1Ref : undefined
                                    }
                                    cursorRef={primaryCursorRef}
                                    drawConfigRef={drawConfigRef}
                                    fillWaterfallViewport
                                    frameCounterRef={frameCounterRef}
                                    height={320}
                                    leftPadPx={leftPadPx}
                                    palette={RX1_PALETTE}
                                    pxPerMHz={pxPerMHz}
                                    pxPerRow={isCompactLayout ? 12 : 16}
                                    ref={primaryCanvasRef}
                                    rightPadPx={rightPadPx}
                                    scaleFromParentHeight
                                    showAverage
                                    showLatest
                                    showPeak
                                    spectrumCssH={isCompactLayout ? 140 : 180}
                                    sweepRowsRef={sweepRowsRef}
                                    totalMHz={totalMHz}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                                <SpectrumWaterfallCanvas
                                    alertMarkersRef={
                                        showAlerts && baselineCompatibility === 'match' ? alertMarkersRx2Ref : undefined
                                    }
                                    baselineChannel="rx2"
                                    cursorRef={secondaryCursorRef}
                                    drawConfigRef={drawConfigRef}
                                    fillWaterfallViewport
                                    frameCounterRef={frameCounterRef}
                                    height={320}
                                    leftPadPx={leftPadPx}
                                    palette={RX2_PALETTE}
                                    pxPerMHz={pxPerMHz}
                                    pxPerRow={isCompactLayout ? 12 : 16}
                                    ref={secondaryCanvasRef}
                                    rightPadPx={rightPadPx}
                                    scaleFromParentHeight
                                    showAverage
                                    showLatest
                                    showPeak
                                    spectrumCssH={isCompactLayout ? 140 : 180}
                                    sweepRowsRef={rx2RowsRef}
                                    totalMHz={totalMHz}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Activity>
    );
}
